import React from 'react';
import {Outlet, useLocation} from 'react-router-dom';
import {styled} from '@mui/material';
import {designSystemToken} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import {ROUTES} from '@/config/routesConstants';
import NavLink from '@/components/nav-link';
import Page from '@/components/page';
import useSubscriptionQuery from '@/hooks/queries/use-subscription-query';
import useBrandId from '@/hooks/use-brand-id';
import useEmberModalsStackStore from '@/stores/emberModalsStackStore';
import styles from './OrganizationSettings.module.scss';

const StyledNavLink = styled(NavLink)(() => ({
  color: designSystemToken('semantic.fg.neutral'),
  height: '30px',
}));

const activeStyle = () => ({
  boxShadow: `inset 0 -3px 0 0 ${designSystemToken('semantic.bg.brand')}`,
  color: `${designSystemToken('semantic.fg.brand')} !important`,
});

function OrganizationSettings({
  basePath = ROUTES.ORGANIZATION_SETTINGS,
}: {
  basePath?: string;
}) {
  const brandId = useBrandId();
  const {subscription} = useSubscriptionQuery({brandId: brandId ?? undefined});
  const location = useLocation();
  const isCampaignSubscription = subscription?.subscriptionType === 'campaign';
  const {modalsStack: emberModalsStack} = useEmberModalsStackStore();
  // Lift the tab nav above the Ember iframe (z-index 2) so links remain
  // clickable on Ember sub-routes; drop back to 0 while an Ember modal is
  // open so the modal renders above the React tabs.
  const tabNavZIndex = emberModalsStack.length ? 0 : 10;

  return (
    <Page
      id="organization-settings"
      className={styles.container}
      childrenClassName={styles.content}
    >
      <div>
        <nav
          className={styles.tabNavigation}
          style={{position: 'relative', zIndex: tabNavZIndex}}
        >
          <StyledNavLink
            to={`${basePath}/brands`}
            style={({isActive}) =>
              isActive || location.pathname === basePath
                ? activeStyle()
                : undefined
            }
          >
            {translate('Brands')}
          </StyledNavLink>
          <StyledNavLink
            to={`${basePath}/users`}
            style={({isActive}) => (isActive ? activeStyle() : undefined)}
          >
            {translate('Users')}
          </StyledNavLink>
          {!isCampaignSubscription && (
            <StyledNavLink
              to={`${basePath}/plan-billing`}
              style={({isActive}) => (isActive ? activeStyle() : undefined)}
            >
              {translate('Plan & Billing')}
            </StyledNavLink>
          )}
          <StyledNavLink
            to={`${basePath}/purchase-history`}
            style={({isActive}) => (isActive ? activeStyle() : undefined)}
          >
            {translate('Purchase History')}
          </StyledNavLink>
        </nav>
        <Outlet />
      </div>
    </Page>
  );
}

export default OrganizationSettings;
